<template>
  <div :class="colClass" :data-field-key="fieldKey || undefined">
    <!-- text / email -->
    <v-text-field
      v-if="field.type === 'text' || field.type === 'email'"
      :model-value="modelValue"
      :label="computedLabel"
      :placeholder="computedPlaceholder"
      :hint="computedHint"
      :maxlength="computedMaxlength"
      :type="field.type === 'email' ? 'email' : 'text'"
      :required="field.required"
      :readonly="readonly || field.readonly"
      :rules="fieldRules"
      :error="displayError"
      :error-messages="displayErrorMessages"
      :counter="field.charCounter ? computedMaxlength : undefined"
      :class="{ 'text-uppercase': field.uppercase }"
      variant="outlined"
      :density="density"
      persistent-hint
      @update:model-value="handleInput"
    >
      <template v-if="field.required" #label>
        {{ computedLabel }} <span class="text-error">*</span>
      </template>
      <template #message="{ message }">
        <span v-if="message" class="text-error">{{ message }}</span>
        <span v-else-if="computedHint" class="text-medium-emphasis">{{ computedHint }}</span>
      </template>
      <!-- 옵션: 우측 inline 액션 아이콘 (예: entity 선택 챗봇 트리거).
           field.appendInnerIcon 이 있을 때만 노출 — 다른 폼은 메타 없으면 비활성.
           PreSet AGM 패널과 동일한 v-btn icon 패턴 (size x-small, variant text). -->
      <template v-if="field.appendInnerIcon" #append-inner>
        <v-btn
          :icon="field.appendInnerIcon"
          :color="field.appendInnerIconColor || 'primary'"
          size="x-small"
          variant="text"
          density="comfortable"
          @click.stop="emit('field-action', { action: field.appendInnerAction || field.id, fieldId: field.id })"
        />
      </template>
    </v-text-field>

    <!-- number -->
    <v-text-field
      v-else-if="field.type === 'number'"
      :model-value="modelValue"
      :label="computedLabel"
      :min="field.min"
      :step="field.step"
      :readonly="readonly || field.readonly"
      :required="field.required"
      :rules="fieldRules"
      :error="displayError"
      :error-messages="displayErrorMessages"
      type="number"
      variant="outlined"
      :density="density"
      @update:model-value="handleNumberInput"
    >
      <template v-if="field.required" #label>
        {{ computedLabel }} <span class="text-error">*</span>
      </template>
    </v-text-field>

    <!-- date - 직접 입력 + 브라우저 기본 달력, 표시는 사용자 날짜 포맷.
         저장은 항상 ISO(YYYY-MM-DD) 다 - CRS 변환기가 숫자 구분자 포맷을 DD/MM 으로
         가정해서 읽기 때문에, 저장값이 사용자 포맷을 따라다니면 MM/DD/YYYY 설정에서
         월과 일이 뒤바뀐 채 제출된다. LDateField 가 그 분리를 담당한다. -->
    <LDateField
      v-else-if="field.type === 'date'"
      :model-value="modelValue"
      @update:model-value="emit('update:modelValue', $event)"
      :label="computedLabel"
      :required="field.required"
      :readonly="readonly"
      :error="displayError"
      :error-messages="displayErrorMessages"
      :placeholder="field.placeholder"
      :hint="computedHint"
      :max="field.maxDate === 'today' ? todayIso() : undefined"
      variant="outlined"
      :density="density"
      hide-details="auto"
    />

    <!-- select: use v-autocomplete when items > 4 for searchability -->
    <v-autocomplete
      v-else-if="field.type === 'select' && selectItems.length > 4"
      :model-value="modelValue"
      :label="computedLabel"
      :items="selectItems"
      :placeholder="field.placeholder || field.selectPlaceholder"
      :required="field.required"
      :readonly="readonly || field.readonly"
      :disabled="field.disabled"
      :rules="fieldRules"
      :error="displayError"
      :error-messages="displayErrorMessages"
      :hint="computedHint"
      :persistent-hint="!!computedHint"
      item-title="label"
      item-value="value"
      variant="outlined"
      :density="density"
      :menu-props="{ scrollStrategy: 'close' }"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <template v-if="field.required" #label>
        {{ computedLabel }} <span class="text-error">*</span>
      </template>
    </v-autocomplete>

    <!-- select: plain v-select for 4 or fewer items -->
    <v-select
      v-else-if="field.type === 'select'"
      :model-value="modelValue"
      :label="computedLabel"
      :items="selectItems"
      :placeholder="field.placeholder || field.selectPlaceholder"
      :required="field.required"
      :readonly="readonly || field.readonly"
      :disabled="field.disabled"
      :rules="fieldRules"
      :error="displayError"
      :error-messages="displayErrorMessages"
      :hint="computedHint"
      :persistent-hint="!!computedHint"
      item-title="label"
      item-value="value"
      variant="outlined"
      :density="density"
      :menu-props="{ scrollStrategy: 'close' }"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <template v-if="field.required" #label>
        {{ computedLabel }} <span class="text-error">*</span>
      </template>
    </v-select>

    <!-- autocomplete -->
    <v-autocomplete
      v-else-if="field.type === 'autocomplete'"
      :model-value="modelValue"
      :label="computedLabel"
      :items="autocompleteItems"
      :item-title="autocompleteItemTitle"
      :item-value="autocompleteItemValue"
      :placeholder="field.placeholder"
      :required="field.required"
      :readonly="readonly"
      :rules="fieldRules"
      :error="displayError"
      :error-messages="displayErrorMessages"
      variant="outlined"
      :density="density"
      :menu-props="{ scrollStrategy: 'close' }"
      clearable
      @update:model-value="emit('update:modelValue', $event)"
    >
      <template v-if="field.required" #label>
        {{ computedLabel }} <span class="text-error">*</span>
      </template>
    </v-autocomplete>

    <!-- textarea (with optional file upload) -->
    <div v-else-if="field.type === 'textarea'">
      <v-textarea
        :model-value="modelValue"
        :label="computedLabel"
        :placeholder="field.placeholder"
        :hint="computedHint"
        :maxlength="field.maxlength"
        :rows="field.rows || 2"
        :required="field.required"
        :readonly="readonly"
        :rules="fieldRules"
        :error="displayError"
        :error-messages="displayErrorMessages"
        :counter="field.charCounter ? field.maxlength : (field.maxlength || undefined)"
        variant="outlined"
        :density="density"
        persistent-hint
        auto-grow
        @update:model-value="emit('update:modelValue', $event)"
      >
        <template v-if="field.required" #label>
          {{ computedLabel }} <span class="text-error">*</span>
        </template>
        <template v-if="field.required" #message="{ message }">
          <span v-if="message" class="text-error">{{ message }}</span>
          <span v-else-if="computedHint" class="text-medium-emphasis">{{ computedHint }}</span>
        </template>
      </v-textarea>
      <!--
        CRS 의 Nature of Business 는 글자수가 아니라 **단어수**(200 words)로 센다.
        maxlength 로만 막으면 2000자 안에서 200단어를 넘겨 CRS 가 거부한다.
      -->
      <div v-if="field.maxWords" class="d-flex justify-end mt-1">
        <span class="text-caption" :class="wordCount > field.maxWords ? 'text-error' : 'text-medium-emphasis'">
          {{ wordCount }} / {{ field.maxWords }} {{ tl('words') }}
        </span>
      </div>
      <v-file-input
        v-if="field.fileUpload && !readonly && (!field.fileUpload.showWhenFilled || hasTextValue)"
        :label="tl(field.fileUpload.label || 'Attachment')"
        :accept="field.fileUpload.accept"
        :hint="tl(field.fileUpload.hint || '')"
        :error-messages="displayErrorMessages || fileValidationError || undefined"
        :error="displayError || !!fileValidationError"
        variant="outlined"
        :density="density"
        persistent-hint
        prepend-icon="mdi-paperclip"
        class="mt-1"
        @update:model-value="handleFileUpload"
      />
    </div>

    <!-- state-toggle: v-autocomplete when country=MALAYSIA, v-text-field otherwise -->
    <v-autocomplete
      v-else-if="field.type === 'state-toggle' && isCountryMalaysia"
      :model-value="modelValue"
      :label="computedLabel"
      :items="stateSelectItems"
      :placeholder="field.selectPlaceholder"
      :required="field.required"
      :readonly="readonly"
      :rules="fieldRules"
      :error="displayError"
      :error-messages="displayErrorMessages"
      item-title="label"
      item-value="value"
      variant="outlined"
      :density="density"
      :menu-props="{ scrollStrategy: 'close' }"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <template v-if="field.required" #label>
        {{ computedLabel }} <span class="text-error">*</span>
      </template>
    </v-autocomplete>
    <v-text-field
      v-else-if="field.type === 'state-toggle' && !isCountryMalaysia"
      :model-value="modelValue"
      :label="computedLabel"
      :placeholder="field.textPlaceholder"
      :required="field.required"
      :readonly="readonly"
      :rules="fieldRules"
      :error="displayError"
      :error-messages="displayErrorMessages"
      variant="outlined"
      :density="density"
      @update:model-value="handleInput"
    >
      <template v-if="field.required" #label>
        {{ computedLabel }} <span class="text-error">*</span>
      </template>
    </v-text-field>

    <!-- checkbox -->
    <v-checkbox
      v-else-if="field.type === 'checkbox'"
      :model-value="modelValue"
      :label="computedLabel"
      :hint="computedHint"
      :readonly="readonly"
      :disabled="field.disabled"
      :color="field.highlight ? 'primary' : undefined"
      :error="displayError"
      :error-messages="displayErrorMessages"
      density="compact"
      persistent-hint
      @update:model-value="emit('update:modelValue', $event)"
    />

    <!-- file -->
    <v-file-input
      v-else-if="field.type === 'file'"
      :label="computedLabel"
      :accept="field.accept"
      :hint="computedHint"
      :required="field.required"
      :readonly="readonly"
      :rules="fieldRules"
      :error="displayError || !!fileValidationError"
      :error-messages="displayErrorMessages || fileValidationError || undefined"
      variant="outlined"
      :density="density"
      persistent-hint
      prepend-icon="mdi-file-upload"
      @update:model-value="handleFileUpload"
    >
      <template v-if="field.required" #label>
        {{ computedLabel }} <span class="text-error">*</span>
      </template>
    </v-file-input>

    <!-- entity-search (v-autocomplete for entities) -->
    <v-autocomplete
      v-else-if="field.type === 'entity-search'"
      :model-value="entitySearchValue"
      :items="entities"
      item-title="companyName"
      item-value="entityCd"
      variant="outlined"
      :density="density"
      :rules="fieldRules"
      :error="displayError"
      :error-messages="displayErrorMessages"
      :loading="entitiesLoading"
      :readonly="readonly"
      :menu-props="{ scrollStrategy: 'close' }"
      clearable
      @update:model-value="emit('entity-change', $event)"
    >
      <template #label>
        {{ tl(field.label) }} <span v-if="field.required" class="text-error">*</span>
      </template>
      <template #message="{ message }">
        <span v-if="message" class="text-error">{{ message }}</span>
        <span v-else-if="field.hint" class="text-medium-emphasis">{{ tl(field.hint) }}</span>
      </template>
      <template #item="{ props: itemProps, item }">
        <v-list-item v-bind="itemProps" :title="undefined">
          <v-list-item-title>{{ item.raw.companyName }}</v-list-item-title>
          <v-list-item-subtitle>
            <span v-if="item.raw.regNo">{{ item.raw.regNo }}</span>
            <span v-if="item.raw.entityCd" class="ml-2 text-caption">({{ item.raw.entityCd }})</span>
          </v-list-item-subtitle>
        </v-list-item>
      </template>
      <template #no-data>
        <v-list-item>
          <v-list-item-title class="text-grey">{{ tl('No entities found') }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-autocomplete>

    <!-- company-select (v-select for REG_NO list) -->
    <v-select
      v-else-if="field.type === 'company-select'"
      :model-value="modelValue"
      :items="entities"
      item-title="display"
      item-value="regNo"
      variant="outlined"
      :density="density"
      :rules="fieldRules"
      :error="displayError"
      :error-messages="displayErrorMessages"
      :loading="entitiesLoading"
      :readonly="readonly"
      clearable
      :menu-props="{ scrollStrategy: 'close' }"
      :placeholder="computedPlaceholder"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <template #label>
        {{ tl(field.label) }} <span v-if="field.required" class="text-error">*</span>
      </template>
      <template #message="{ message }">
        <span v-if="message" class="text-error">{{ message }}</span>
        <span v-else-if="field.hint" class="text-medium-emphasis">{{ tl(field.hint) }}</span>
      </template>
      <template #item="{ props: itemProps, item }">
        <v-list-item v-bind="itemProps" :title="undefined">
          <v-list-item-title>{{ item.raw.display || item.raw.regNo }}</v-list-item-title>
          <v-list-item-subtitle v-if="item.raw.companyName && !item.raw.display">
            {{ item.raw.companyName }}
          </v-list-item-subtitle>
        </v-list-item>
      </template>
      <template #no-data>
        <v-list-item>
          <v-list-item-title class="text-grey">{{ tl('No entities found') }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-select>

    <!-- officer-select (v-select for officers) -->
    <v-select
      v-else-if="field.type === 'officer-select'"
      :model-value="modelValue"
      :items="entities"
      item-title="display"
      item-value="contactRegNo"
      variant="outlined"
      :density="density"
      :rules="fieldRules"
      :error="displayError"
      :error-messages="displayErrorMessages"
      :loading="entitiesLoading"
      :readonly="readonly"
      clearable
      :menu-props="{ scrollStrategy: 'close' }"
      :placeholder="computedPlaceholder"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <template #label>
        {{ tl(field.label) }} <span v-if="field.required" class="text-error">*</span>
      </template>
      <template #message="{ message }">
        <span v-if="message" class="text-error">{{ message }}</span>
        <span v-else-if="field.hint" class="text-medium-emphasis">{{ tl(field.hint) }}</span>
      </template>
      <template #item="{ props: itemProps, item }">
        <v-list-item v-bind="itemProps" :title="undefined">
          <v-list-item-title>{{ item.raw.display || item.raw.contactRegNo }}</v-list-item-title>
          <v-list-item-subtitle v-if="item.raw.contactName && !item.raw.display">
            {{ item.raw.contactName }}
          </v-list-item-subtitle>
        </v-list-item>
      </template>
      <template #no-data>
        <v-list-item>
          <v-list-item-title class="text-grey">{{ tl('No contacts found') }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-select>

    <!-- contact-search (v-combobox for contacts) -->
    <v-combobox
      v-else-if="field.type === 'contact-search'"
      :model-value="modelValue"
      :items="contacts"
      item-title="contactName"
      item-value="contactName"
      variant="outlined"
      :density="density"
      :loading="contactsLoading"
      :rules="fieldRules"
      :error="displayError"
      :error-messages="displayErrorMessages"
      :readonly="readonly"
      clearable
      autocomplete="off"
      :class="{ 'text-uppercase': field.uppercase }"
      :placeholder="field.placeholder || contactPlaceholder"
      @update:model-value="handleContactChange"
    >
      <template #label>
        {{ tl(field.label) }} <span v-if="field.required" class="text-error">*</span>
      </template>
      <template #message="{ message }">
        <span v-if="message" class="text-error">{{ message }}</span>
        <span v-else-if="field.hint || contactHint" class="text-medium-emphasis">{{ tl(field.hint || contactHint) }}</span>
      </template>
      <template #item="{ props: itemProps, item }">
        <v-list-item v-bind="itemProps" :title="undefined">
          <template #prepend>
            <v-avatar color="primary" size="32">
              <v-icon size="16" color="white">mdi-account</v-icon>
            </v-avatar>
          </template>
          <v-list-item-title>{{ item.raw.contactName }}</v-list-item-title>
          <v-list-item-subtitle>
            <span v-if="item.raw.contactRegNo">{{ item.raw.contactRegNo }}</span>
            <span v-if="item.raw.contactEmail" class="ml-2">&bull; {{ item.raw.contactEmail }}</span>
          </v-list-item-subtitle>
        </v-list-item>
      </template>
      <template #no-data>
        <v-list-item>
          <v-list-item-title class="text-grey">{{ tl('No contacts found. You can type to enter manually.') }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-combobox>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import LDateField from '@shared/components/LDateField.vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import type { FieldDef, RefDataItem } from '../composables/useFormRenderer';

// Translation helper
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

interface Props {
  field: FieldDef;
  modelValue: any;
  prefix?: string;
  readonly?: boolean;
  refData?: Record<string, RefDataItem[]>;
  density?: 'compact' | 'comfortable';
  // For state-toggle: current country value from parent
  countryValue?: string;
  // For dynamicLabel: current linked field value
  linkedFieldValue?: string;
  // entity-search props
  entities?: any[];
  entitiesLoading?: boolean;
  entitySearchValue?: string | null;
  // contact-search props
  contacts?: any[];
  contactsLoading?: boolean;
  contactPlaceholder?: string;
  contactHint?: string;
  // date props
  datePickerValue?: Date | null;
  /** 외부 validate() 시 필드별 빨간 강조용 */
  errorMessage?: string;
  /** scrollIntoView 타겟 식별자 */
  fieldKey?: string;
  /** 폼 validate() 후 rules 기반 에러 즉시 표시 */
  showErrors?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  prefix: '',
  readonly: false,
  refData: () => ({}),
  density: 'compact',
  countryValue: 'MALAYSIA',
  linkedFieldValue: '',
  entities: () => [],
  entitiesLoading: false,
  entitySearchValue: null,
  contacts: () => [],
  contactsLoading: false,
  contactPlaceholder: '',
  contactHint: '',
  showErrors: false,
  datePickerValue: null,
  errorMessage: '',
  fieldKey: '',
});

const emit = defineEmits<{
  'update:modelValue': [value: any];
  'file-change': [payload: { fieldId: string; file: File | null }];
  'entity-change': [entityCd: string | null];
  'contact-change': [value: any];
  'date-picker-change': [value: any];
  'date-blur': [];
  'field-action': [payload: { action: string; fieldId: string }];
}>();

/** v-menu date picker open state */
const dateMenuOpen = ref(false);
/**
 * maxDate="today" 필드의 상한.
 *
 * toISOString() 은 UTC 라서 UTC+8(말레이시아) 오전에는 **어제**가 상한이 됐다 — 선임일·
 * 해임일처럼 "오늘"을 고르는 칸에서 달력이 오늘을 막았다. 로컬 날짜로 계산하고,
 * 함수로 두어 화면을 켜 둔 채 자정을 넘겨도 갱신되게 한다.
 */
function todayIso(): string {
  const d = new Date();
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

// ── Date parsing helper — handles all user dateFormat variants + legacy formats ──
const DATE_MONTH_FULL: Record<string, number> = {
  JANUARY: 0, FEBRUARY: 1, MARCH: 2, APRIL: 3, MAY: 4, JUNE: 5,
  JULY: 6, AUGUST: 7, SEPTEMBER: 8, OCTOBER: 9, NOVEMBER: 10, DECEMBER: 11,
};
const DATE_MONTH_ABBR: Record<string, number> = {
  JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
  JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
};

function tryParseAnyDate(v: string): Date | null {
  if (!v) return null;
  const s = v.trim();
  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  // "DD MONTH YYYY" or "DD Mon. YYYY"
  const mnMatch = s.match(/^(\d{1,2})\s+([A-Za-z.]+)\s+(\d{4})$/);
  if (mnMatch) {
    const mc = mnMatch[2].replace(/\./g, '').toUpperCase();
    const mi = DATE_MONTH_FULL[mc] ?? DATE_MONTH_ABBR[mc] ?? -1;
    if (mi >= 0) return new Date(+mnMatch[3], mi, +mnMatch[1]);
  }
  // Numeric with separator (/, -, .) — use user's dateFormat to determine order
  const sep = s.match(/[/.\-]/)?.[0];
  if (sep) {
    const parts = s.split(sep).map(p => p.trim());
    if (parts.length === 3 && parts.every(p => /^\d+$/.test(p))) {
      let userFmt = 'DD/MM/YYYY';
      try { userFmt = useSessionStore().user?.user?.dateFormat || 'DD/MM/YYYY'; } catch {}
      const fmt = userFmt.toUpperCase();
      let y: number, m: number, d: number;
      if (fmt.startsWith('YYYY')) { y = +parts[0]; m = +parts[1]; d = +parts[2]; }
      else if (fmt.startsWith('MM')) { m = +parts[0]; d = +parts[1]; y = +parts[2]; }
      else { d = +parts[0]; m = +parts[1]; y = +parts[2]; }
      if (y > 0 && m >= 1 && m <= 12 && d >= 1 && d <= 31) return new Date(y, m - 1, d);
    }
  }
  // Fallback
  const parsed = new Date(s);
  return isNaN(parsed.getTime()) ? null : parsed;
}

// Dynamic label from linked field (idType -> label)
const dynamicConfig = computed(() => {
  if (!props.field.dynamicLabel || !props.linkedFieldValue) return null;
  const ds = props.refData[props.field.dynamicLabel.dataSource];
  if (!ds) return null;
  return ds.find(item => item.value === props.linkedFieldValue) || null;
});

const computedLabel = computed(() => {
  if (dynamicConfig.value) {
    const keys = props.field.dynamicLabel!.keys;
    return tl(dynamicConfig.value[keys.label] || props.field.label);
  }
  return tl(props.field.label);
});

const computedPlaceholder = computed(() => {
  if (dynamicConfig.value) {
    const keys = props.field.dynamicLabel!.keys;
    return dynamicConfig.value[keys.placeholder] || props.field.placeholder || '';
  }
  return props.field.placeholder || '';
});

const computedHint = computed(() => {
  if (dynamicConfig.value) {
    const keys = props.field.dynamicLabel!.keys;
    return tl(dynamicConfig.value[keys.desc] || props.field.hint || '');
  }
  return tl(props.field.hint || '');
});

const computedMaxlength = computed(() => {
  if (dynamicConfig.value) {
    const keys = props.field.dynamicLabel!.keys;
    return dynamicConfig.value[keys.maxlength] || props.field.maxlength;
  }
  return props.field.maxlength;
});

// Date field placeholder/hint — derived from user's dateFormat
const datePlaceholder = computed(() => {
  let fmt = 'DD/MM/YYYY';
  try { fmt = useSessionStore().user?.user?.dateFormat || 'DD/MM/YYYY'; } catch {}
  return fmt.toUpperCase();
});
const dateHint = computed(() => {
  const fmt = datePlaceholder.value;
  // Generate example: replace tokens with sample date 15 Jan 1990
  const example = fmt
    .replace(/YYYY/gi, '1990').replace(/YY/gi, '90')
    .replace(/MMMM/g, 'January').replace(/MMM/g, 'Jan.')
    .replace(/MM/g, '01').replace(/DD/gi, '15')
    .replace(/\bD\b/gi, '15');
  return tl('e.g.') + ' ' + example;
});

// Column class (12-grid colSpan → CSS grid span)
const colClass = computed(() => {
  const span = props.field.colSpan || 1;
  if (span >= 12) return 'field-col-full';
  if (span >= 6) return 'field-col-2';
  return 'field-col-1';
});

// Select items (with label translation for inline options)
/**
 * 저장된 값이 선택지에 없으면 그 값을 **선택지에 얹어** 보이게 한다.
 *
 * Vuetify 의 select/autocomplete 는 items 에 없는 model-value 를 그냥 빈칸으로 그린다.
 * 값은 남아 있는데 화면만 비어 보이므로 사용자는 무엇이 들어 있는지 알 수 없고,
 * required 검증도 값이 있으니 통과한다 — 그대로 제출되면 CRS 로 잘못된 값이 나간다.
 * (실제 사례: 연락처의 ID_TYPE_CD 가 M01006 이면 폼이 'POLICE_ID' 를 넣는데, CRS 1차
 *  신분증 목록에는 Police ID 가 없어 선택지에서 빠져 있다.)
 */
function withCurrentValue(items: any[]): any[] {
  const cur = props.modelValue;
  if (cur === null || cur === undefined || cur === '') return items;
  if (items.some((it: any) => (it && typeof it === 'object' ? it.value : it) === cur)) return items;
  return [...items, { value: cur, label: String(cur) }];
}

const selectItems = computed(() => {
  let items: any[] = [];
  if (props.field.options) {
    items = props.field.options;
  } else if (props.field.dataSource && props.refData[props.field.dataSource]) {
    const data = props.refData[props.field.dataSource];
    // Support nested data structure (e.g., companySubtypes by companyType)
    if (props.field.dataSourceKey && typeof data === 'object' && !Array.isArray(data)) {
      items = (data as Record<string, any>)[props.field.dataSourceKey] || [];
    } else if (Array.isArray(data)) {
      // dataSource items (reference data) - return as is without translation
      return withCurrentValue(data);
    }
  }
  // Inline options are code data (e.g., "NEW INCORPORATION", "LIMITED BY SHARES") - do not translate
  return withCurrentValue(items);
});

// Autocomplete items
const autocompleteItems = computed(() => {
  const source = props.field.datalistSource;
  if (!source || !props.refData[source]) return [];
  const data = props.refData[source];
  if (props.field.datalistFormat) {
    return data.map(item => {
      const label = props.field.datalistFormat!
        .replace('{code}', item.code || '')
        .replace('{desc}', item.desc || '');
      return { value: item.code || item.value, label };
    });
  }
  return data;
});

/**
 * 이 필드에 실제 값이 있는가.
 * CRS 는 Clarification 에 **답을 적었을 때만** 첨부 입력을 띄운다 → 같은 규칙을 쓰려고 둔다.
 */
const hasTextValue = computed(() => String(props.modelValue ?? '').trim().length > 0);

/** CRS 가 단어수로 세는 항목(Nature of Business)용. 공백 구분 토큰 수. */
const wordCount = computed(() => {
  const s = String(props.modelValue ?? '').trim();
  return s ? s.split(/\s+/).length : 0;
});

const autocompleteItemTitle = computed(() => 'label');
const autocompleteItemValue = computed(() => 'value');

// State toggle
const isCountryMalaysia = computed(() => props.countryValue === 'MALAYSIA');

const stateSelectItems = computed(() => {
  const source = props.field.selectDataSource;
  if (!source || !props.refData[source]) return [];
  return props.refData[source];
});

// Validation rules
const fieldRules = computed(() => {
  const rules: ((v: any) => boolean | string)[] = [];
  if (props.field.required && !props.readonly) {
    rules.push((v: any) => !!v || `${computedLabel.value} ${tl('is required')}`);
  }
  if (props.field.type === 'email' && !props.readonly) {
    rules.push((v: string) => {
      if (!v) return true;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || tl('Please enter a valid email address');
    });
  }
  if (props.field.type === 'date' && !props.readonly) {
    // Accept any date-like string that can be parsed (user's dateFormat, ISO, month names, etc.)
    rules.push((v: string) => {
      if (!v) return true;
      if (tryParseAnyDate(v)) return true;
      return tl('Please enter a valid date');
    });
    // maxDate="today" → date cannot be in the future
    if (props.field.maxDate === 'today') {
      rules.push((v: string) => {
        if (!v) return true;
        const d = tryParseAnyDate(v);
        if (!d) return true;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        d.setHours(0, 0, 0, 0);
        if (d > today) return tl('Date cannot be greater than today');
        return true;
      });
    }
  }
  return rules;
});

// Error messages shown when showErrors is true and field fails rules
const ruleErrorMessages = computed((): string[] => {
  if (!props.showErrors) return [];
  const msgs: string[] = [];
  for (const rule of fieldRules.value) {
    const result = rule(props.modelValue);
    if (typeof result === 'string') msgs.push(result);
  }
  return msgs;
});

const displayError = computed(() => !!props.errorMessage || ruleErrorMessages.value.length > 0);
const displayErrorMessages = computed(() => {
  if (props.errorMessage) return props.errorMessage;
  if (ruleErrorMessages.value.length > 0) return ruleErrorMessages.value;
  return undefined;
});

// Handlers
function handleInput(val: any) {
  if (props.field.uppercase && typeof val === 'string') {
    emit('update:modelValue', val.toUpperCase());
  } else {
    emit('update:modelValue', val);
  }
}

function handleNumberInput(val: any) {
  emit('update:modelValue', val !== '' && val !== null ? Number(val) : null);
}

// 파일 매직 바이트 시그니처 (위변조 검증용)
const FILE_MAGIC_BYTES: Record<string, number[]> = {
  '.pdf':  [0x25, 0x50, 0x44, 0x46],       // %PDF
  '.jpg':  [0xFF, 0xD8, 0xFF],              // JFIF/EXIF
  '.jpeg': [0xFF, 0xD8, 0xFF],
  '.png':  [0x89, 0x50, 0x4E, 0x47],        // .PNG
  '.docx': [0x50, 0x4B, 0x03, 0x04],        // PK (ZIP)
  '.zip':  [0x50, 0x4B, 0x03, 0x04],
};

async function validateFileMagicBytes(file: File, accept: string): Promise<boolean> {
  if (!accept) return true;
  const allowedExts = accept.split(',').map(s => s.trim().toLowerCase());

  // accept에 해당하는 매직 바이트 목록 수집
  const expectedSignatures: { ext: string; magic: number[] }[] = [];
  for (const ext of allowedExts) {
    const magic = FILE_MAGIC_BYTES[ext];
    if (magic) expectedSignatures.push({ ext, magic });
  }
  if (expectedSignatures.length === 0) return true; // 매직 바이트 정의 없으면 스킵

  // 파일 첫 8바이트 읽기
  const slice = file.slice(0, 8);
  const buffer = await slice.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  // 매직 바이트가 하나라도 일치하면 통과
  return expectedSignatures.some(({ magic }) =>
    magic.every((b, i) => bytes[i] === b)
  );
}

const fileValidationError = ref('');

async function handleFileUpload(files: File | File[] | null) {
  const file = Array.isArray(files) ? files[0] || null : files;
  fileValidationError.value = '';

  if (file) {
    // CRS 제한: 첨부 1개, 3MB 이하 (폼 문구 "Only 1 attachment, not more than 3MB")
    const maxBytes = (props.field.fileUpload?.maxSizeMb ?? 3) * 1024 * 1024;
    if (file.size > maxBytes) {
      fileValidationError.value =
        `${tl('File is too large')} (${(file.size / 1024 / 1024).toFixed(1)}MB > ${maxBytes / 1024 / 1024}MB)`;
      emit('file-change', { fieldId: props.field.id, file: null });
      return;
    }
    const accept = props.field.fileUpload?.accept || props.field.accept || '';
    const isValid = await validateFileMagicBytes(file, accept);
    if (!isValid) {
      fileValidationError.value = tl('File content does not match the expected type. The file may be forged.');
      emit('file-change', { fieldId: props.field.id, file: null });
      return;
    }
  }

  emit('file-change', { fieldId: props.field.id, file });
}

function handleContactChange(val: any) {
  // v-combobox may return the raw Contact object when selecting from dropdown
  // Emit contact-change with the original value (object or string) for auto-fill logic
  emit('contact-change', val);
  // Also emit update:modelValue with a string to prevent [object Object] display
  if (typeof val === 'object' && val !== null) {
    const name = val.contactName || '';
    emit('update:modelValue', props.field.uppercase ? name.toUpperCase() : name);
  } else {
    const str = val || '';
    emit('update:modelValue', props.field.uppercase ? str.toUpperCase() : str);
  }
}

function handleDatePick(val: any) {
  dateMenuOpen.value = false;
  emit('date-picker-change', val);
}

</script>

<style scoped>
.field-col-full {
  grid-column: 1 / -1;
}
.field-col-2 {
  grid-column: span 2;
}
.field-col-1 {
  grid-column: span 1;
}
.text-uppercase :deep(input) {
  text-transform: uppercase;
}
</style>
